'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';
import {
  RefreshCw,
  Users,
  Lightbulb,
  Target,
  Clock,
  Heart,
  AlertCircle,
  BookOpen,
  MessageSquare,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Purpose =
  | 'Informational Interview'
  | 'Job Referral'
  | 'Industry Advice'
  | 'Mentorship'
  | 'Alumni Connection'
  | 'Conference Follow-up';

type DiscoverySource =
  | 'LinkedIn'
  | 'Mutual Connection'
  | 'Company Website'
  | 'Conference'
  | 'Article / Talk';

type Tone = 'Professional' | 'Casual' | 'Warm';

interface GeneratedEmail {
  subject: string;
  body: string;
  variationIndex: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const purposes: Purpose[] = [
  'Informational Interview',
  'Job Referral',
  'Industry Advice',
  'Mentorship',
  'Alumni Connection',
  'Conference Follow-up',
];

const discoverySources: DiscoverySource[] = [
  'LinkedIn',
  'Mutual Connection',
  'Company Website',
  'Conference',
  'Article / Talk',
];

const tones: Tone[] = ['Professional', 'Casual', 'Warm'];

// ---------------------------------------------------------------------------
// Helper: Discovery context sentence
// ---------------------------------------------------------------------------

function discoveryIntro(
  source: DiscoverySource,
  recipientName: string,
  recipientTitle: string,
  company: string
): string {
  const name = recipientName || '[Recipient Name]';
  const title = recipientTitle || '[Their Title]';
  const co = company || '[Company]';

  switch (source) {
    case 'LinkedIn':
      return `I came across your profile on LinkedIn and was impressed by your work as ${title} at ${co}.`;
    case 'Mutual Connection':
      return `[Mutual Connection] suggested I reach out to you, speaking very highly of your work as ${title} at ${co}.`;
    case 'Company Website':
      return `While researching ${co}, I came across your profile and was drawn to the work you are doing as ${title}.`;
    case 'Conference':
      return `I had the pleasure of attending your session at [Conference Name] and was inspired by your insights on the industry.`;
    case 'Article / Talk':
      return `I recently read your article [Article/Talk Title] and found your perspective on the field incredibly insightful.`;
  }
}

// ---------------------------------------------------------------------------
// Helper: Tone greeting and sign-off
// ---------------------------------------------------------------------------

function greeting(tone: Tone, recipientName: string): string {
  const name = recipientName || '[Recipient Name]';
  switch (tone) {
    case 'Professional':
      return `Dear ${name},`;
    case 'Casual':
      return `Hi ${name},`;
    case 'Warm':
      return `Hello ${name},`;
  }
}

function signOff(tone: Tone): string {
  switch (tone) {
    case 'Professional':
      return 'Best regards,\n[Your Name]';
    case 'Casual':
      return 'Thanks,\n[Your Name]';
    case 'Warm':
      return 'With appreciation,\n[Your Name]';
  }
}

// ---------------------------------------------------------------------------
// Subject lines (per purpose x tone)
// ---------------------------------------------------------------------------

function getSubjectLines(
  purpose: Purpose,
  tone: Tone,
  recipientTitle: string,
  company: string,
  source: DiscoverySource
): string[] {
  const title = recipientTitle || '[Their Title]';
  const co = company || '[Company]';

  const map: Record<Purpose, Record<Tone, string[]>> = {
    'Informational Interview': {
      Professional: [
        `Request for Informational Interview - ${co}`,
        `Exploring Career Paths: Would Value Your Perspective`,
        `Informational Interview Request - Aspiring ${title}`,
        `Seeking Your Insight on a Career in ${co}'s Industry`,
        `Brief Conversation Request - Learning About ${co}`,
      ],
      Casual: [
        `Quick Question About Working at ${co}`,
        `Would Love to Pick Your Brain About ${co}`,
        `Coffee Chat Request - Curious About Your Career Path`,
        `Fellow Professional Curious About ${co}`,
        `20-Minute Chat? Interested in Your Work at ${co}`,
      ],
      Warm: [
        `Inspired by Your Work at ${co} - Would Love to Connect`,
        `Hoping to Learn From Your Experience at ${co}`,
        `Your Career Journey at ${co} Caught My Eye`,
        `Would Be Grateful for a Brief Conversation About ${co}`,
        `Reaching Out With Admiration - ${co}`,
      ],
    },
    'Job Referral': {
      Professional: [
        `Inquiry About Opportunities at ${co}`,
        `Exploring Roles at ${co} - Would Appreciate Your Guidance`,
        `Interest in Joining ${co} - Seeking Your Advice`,
        `Referral Request - Open Position at ${co}`,
        `Following Up on an Opportunity at ${co}`,
      ],
      Casual: [
        `Interested in Joining the ${co} Team`,
        `Any Openings at ${co}? Would Love Your Input`,
        `Know Anyone Hiring at ${co}?`,
        `Quick Ask About Opportunities at ${co}`,
        `Would Love to Work With Your Team at ${co}`,
      ],
      Warm: [
        `Would Appreciate Your Help Getting Connected at ${co}`,
        `Hoping You Might Point Me in the Right Direction at ${co}`,
        `Your Insight Could Help Me Land a Role at ${co}`,
        `Grateful for Any Guidance on Joining ${co}`,
        `Reaching Out Regarding Opportunities at ${co}`,
      ],
    },
    'Industry Advice': {
      Professional: [
        `Seeking Your Expert Perspective on the Industry`,
        `Professional Inquiry - Navigating the ${co} Sector`,
        `Would Value Your Industry Insight`,
        `Brief Question About Trends in Your Field`,
        `Request for Professional Advice - Industry Landscape`,
      ],
      Casual: [
        `Quick Question About the Industry`,
        `Would Love Your Take on Where the Industry Is Heading`,
        `Curious About Your Experience in the Field`,
        `Fellow Professional Seeking Your Perspective`,
        `Picking Your Brain on Industry Trends`,
      ],
      Warm: [
        `Your Experience Would Mean a Lot to an Aspiring Professional`,
        `Hoping to Learn From Someone Who Knows the Industry`,
        `Inspired by Your Career - Seeking Advice`,
        `Would Be Grateful for Your Perspective on the Industry`,
        `Your Guidance Could Make a Real Difference`,
      ],
    },
    'Mentorship': {
      Professional: [
        `Seeking a Mentor in the ${co} Space`,
        `Mentorship Inquiry - Aspiring ${title}`,
        `Request for Professional Mentorship`,
        `Looking for Guidance - Would You Consider Mentoring?`,
        `Mentorship Opportunity - Eager to Learn From You`,
      ],
      Casual: [
        `Would You Be Open to Mentoring?`,
        `Looking for a Guide in the Industry`,
        `Seeking a Mentor - Your Work at ${co} Inspires Me`,
        `Would Love to Learn From You`,
        `Informal Mentorship Inquiry`,
      ],
      Warm: [
        `Your Career Inspires Me - Hoping for Guidance`,
        `Would Be Honored to Learn From Your Experience`,
        `Seeking a Mentor Who Has Walked This Path`,
        `Your Journey at ${co} Resonates With Me`,
        `A Sincere Request for Mentorship`,
      ],
    },
    'Alumni Connection': {
      Professional: [
        `Fellow [University] Alum - Would Love to Connect`,
        `Alumni Networking - Exploring Careers at ${co}`,
        `Connecting With a Fellow [University] Graduate`,
        `Alumni Introduction - Interest in ${co}`,
        `Fellow Alum Seeking Professional Connection`,
      ],
      Casual: [
        `Hey Fellow [University] Alum!`,
        `Go [Mascot]! Fellow Alum Reaching Out`,
        `Small World - Fellow [University] Grad at ${co}`,
        `Alumni Connection Request - ${co}`,
        `One Alum to Another - Quick Question`,
      ],
      Warm: [
        `Proud Fellow [University] Alum Reaching Out`,
        `Our Shared Alma Mater Inspired Me to Connect`,
        `Fellow [University] Grad - Would Love to Chat`,
        `Bonded by [University] - Hoping to Connect`,
        `From One [University] Alum to Another`,
      ],
    },
    'Conference Follow-up': {
      Professional: [
        `Following Up From [Conference Name]`,
        `Great Meeting You at [Conference Name]`,
        `Post-Conference Follow-Up - Continuing Our Conversation`,
        `Connecting After [Conference Name] - ${co}`,
        `[Conference Name] Follow-Up - Exploring Collaboration`,
      ],
      Casual: [
        `Great Chatting at [Conference Name]!`,
        `Loved Your Talk at [Conference Name]`,
        `Following Up From [Conference] - Let's Stay in Touch`,
        `[Conference Name] Was Great - Wanted to Reconnect`,
        `Quick Follow-Up From [Conference Name]`,
      ],
      Warm: [
        `Truly Enjoyed Our Connection at [Conference Name]`,
        `Inspired by Your Session at [Conference Name]`,
        `Your Talk at [Conference Name] Stayed With Me`,
        `Grateful We Connected at [Conference Name]`,
        `[Conference Name] Highlight - Meeting You`,
      ],
    },
  };

  return map[purpose][tone];
}

// ---------------------------------------------------------------------------
// Body templates (per purpose x tone -- 4-5 variations each)
// ---------------------------------------------------------------------------

function getBodyTemplates(
  purpose: Purpose,
  tone: Tone,
  source: DiscoverySource,
  recipientName: string,
  recipientTitle: string,
  company: string,
  background: string,
  seeking: string
): string[] {
  const name = recipientName || '[Recipient Name]';
  const title = recipientTitle || '[Their Title]';
  const co = company || '[Company]';
  const bg = background || '[your background]';
  const goal = seeking || '[what you are seeking]';
  const disc = discoveryIntro(source, recipientName, recipientTitle, company);
  const greet = greeting(tone, recipientName);
  const sign = signOff(tone);

  // ----- Informational Interview -----
  if (purpose === 'Informational Interview') {
    if (tone === 'Professional') {
      return [
`${greet}

${disc}

My name is [Your Name], and I am ${bg}. I am currently exploring career paths in this field, and your trajectory as ${title} at ${co} is exactly the kind of journey I would like to learn more about.

I would be grateful if you could spare 15 to 20 minutes for a brief conversation. I am interested in ${goal}, and I believe your perspective would be invaluable.

I am happy to work around your schedule and can meet virtually or in person, whichever is most convenient.

${sign}`,

`${greet}

I hope this message finds you well. ${disc}

I am ${bg}, and I am keen to learn more about the day-to-day realities of working at a company like ${co}. Your experience as ${title} makes you an ideal person to speak with.

Specifically, I am hoping to learn about ${goal}. Would you be open to a brief call or coffee meeting in the coming weeks? I promise to be respectful of your time.

Thank you for considering my request.

${sign}`,

`${greet}

${disc} I am reaching out because I am ${bg}, and I am at a point in my career where I would greatly benefit from hearing firsthand about experiences like yours.

I am particularly interested in ${goal}. Even a 15-minute phone call would be tremendously helpful. I have done my research and have specific questions that I think could lead to a focused and productive conversation.

I understand you are busy, so please let me know if and when might work for you.

${sign}`,

`${greet}

${disc}

As someone who is ${bg}, I have been thoughtfully exploring my next career steps. Your role as ${title} at ${co} represents exactly the type of career trajectory I admire.

I would welcome the opportunity to learn about ${goal}. If you have 15 to 20 minutes in the coming weeks, I would be honored to hear your perspective -- whether by phone, video, or over coffee.

Thank you in advance for your time and generosity.

${sign}`,

`${greet}

I am writing to introduce myself and ask for a small favor. ${disc}

I am ${bg}, and I am deeply interested in ${goal}. A brief informational interview with someone of your experience and expertise would help me make more informed decisions about my career direction.

I would be happy to send over specific questions in advance so you can decide whether a conversation would be worthwhile. I appreciate your consideration and look forward to the possibility of connecting.

${sign}`,
      ];
    }

    if (tone === 'Casual') {
      return [
`${greet}

${disc} I am ${bg}, and I am really curious about what it is like working in your space.

I would love to hear about ${goal} if you have 15 or 20 minutes sometime. No pressure at all -- I know how busy things can get. A quick phone call or virtual coffee would be awesome.

Thanks for even reading this far. Hope to chat!

${sign}`,

`${greet}

${disc}

A little about me: I am ${bg}, and I have been doing a lot of thinking about my career direction lately. Your path at ${co} is genuinely fascinating, and I would love to hear how you got there.

I am mainly curious about ${goal}. Would you be up for a quick chat sometime? Totally understand if the timing does not work out.

Either way, thanks for your time!

${sign}`,

`${greet}

Hope you do not mind a cold email! ${disc}

I am ${bg} and I am trying to figure out my next move. I think talking to someone like you could really help me think things through. I am especially interested in ${goal}.

If you have got even 15 minutes to spare, I would love to buy you a virtual coffee and pick your brain. No worries if you can not -- I appreciate you reading this.

${sign}`,

`${greet}

${disc} I stumbled across your work and thought, "I need to talk to this person!"

I am ${bg}, and I am at a crossroads. I have a lot of questions about ${goal}, and I think your experience could really help me see things more clearly.

Would you be open to a quick, low-key chat? I promise to keep it short and sweet.

${sign}`,
      ];
    }

    // Warm
    return [
`${greet}

${disc} I hope you do not mind me reaching out.

I am ${bg}, and your career at ${co} genuinely inspires me. I am at a point where I am exploring new directions, and the path you have taken as ${title} is one I deeply admire.

I would be so grateful if you could spare a few minutes to share your perspective on ${goal}. I understand how valuable your time is, and I would not ask if I did not think this conversation could be meaningful.

Thank you for considering this. It would mean a great deal to me.

${sign}`,

`${greet}

I wanted to reach out because your work has truly resonated with me. ${disc}

I am ${bg}, and I have been reflecting a lot on my career direction. Learning about ${goal} from someone with your depth of experience would be incredibly valuable.

If you are open to a 15-minute conversation, I would be honored. I will come prepared with thoughtful questions and a deep respect for your time.

${sign}`,

`${greet}

${disc}

I am ${bg}, and I have to say, your journey is one that I find both inspiring and relatable. I am hoping to learn more about ${goal}, and I believe a brief conversation with you could genuinely shape my next steps.

I know cold outreach can feel forward, so please know this comes from a place of genuine admiration. I would be grateful for any amount of time you can share.

${sign}`,

`${greet}

${disc} Your career path has genuinely moved me to reach out.

I am ${bg}. As I navigate my own professional journey, I keep coming back to professionals like you whose work I respect deeply. I would love to hear your thoughts on ${goal}.

Even a brief exchange -- whether a call, a few email replies, or a coffee -- would mean the world. Thank you for reading this far.

${sign}`,
    ];
  }

  // ----- Job Referral -----
  if (purpose === 'Job Referral') {
    if (tone === 'Professional') {
      return [
`${greet}

${disc}

My name is [Your Name], and I am ${bg}. I noticed that ${co} has an open position for [Role Title], and I believe my skills and experience are a strong match. I am reaching out because ${goal}.

If you feel comfortable, I would greatly appreciate an internal referral or any guidance on how to make my application stand out. I have attached my resume for your reference and am happy to provide any additional information.

Thank you for considering this request.

${sign}`,

`${greet}

I hope this message finds you well. ${disc}

I am ${bg}, and I am very interested in the [Role Title] position at ${co}. After researching the team and the company's direction, I am convinced this could be an excellent fit.

I am reaching out because ${goal}. Would you be open to a brief conversation about the role and whether you might be willing to put in a good word? I would be grateful for any level of support you are comfortable providing.

${sign}`,

`${greet}

${disc}

I am ${bg}, and I have been following ${co}'s work with great interest. I recently saw the [Role Title] opening and was excited to see how closely it aligns with my experience.

${goal}. I understand that referrals carry significant weight, and I would not ask unless I was confident in my fit for the role. I would welcome the chance to discuss this further if you are open to it.

${sign}`,

`${greet}

${disc} I am writing because I am ${bg}, and I believe I could make a meaningful contribution to your team at ${co}.

I noticed the [Role Title] position and it aligns closely with my background. ${goal}. If you are in a position to provide a referral or connect me with the hiring manager, I would be deeply grateful.

I respect that this is a significant ask, and I am happy to share more about my qualifications before you make any decision.

${sign}`,
      ];
    }

    if (tone === 'Casual') {
      return [
`${greet}

${disc}

I am ${bg}, and I just saw a role at ${co} that looks like it was written for me. I know this is a big ask, but would you be open to referring me or connecting me with the right person?

Here is why I think I would be a great fit: ${goal}. I would love to chat more about it if you are interested.

Totally understand if you are not comfortable with that -- no hard feelings at all!

${sign}`,

`${greet}

${disc} I hope this is not too forward, but I am ${bg}, and I am really excited about the [Role Title] position at ${co}.

I am hoping ${goal}. If you think there might be a fit, would you mind passing along my resume or pointing me to the right person?

Either way, I appreciate your time. Thanks!

${sign}`,

`${greet}

Quick one for you! ${disc}

I am ${bg}, and I noticed ${co} is hiring for [Role Title]. I think I would crush it in that role, and I am hoping ${goal}.

Would you be willing to connect me with the hiring team or put in a word? I would owe you one!

${sign}`,

`${greet}

${disc} Figured I would shoot my shot!

I am ${bg}, and the [Role Title] opening at ${co} has my name written all over it. I am hoping ${goal}. If you could pass along my info or give me a warm intro, that would be incredible.

No pressure though -- I know referrals are personal. Thanks for even considering it!

${sign}`,
      ];
    }

    // Warm
    return [
`${greet}

${disc} I hope you do not mind me reaching out with a request.

I am ${bg}, and I recently discovered the [Role Title] opening at ${co}. The more I have learned about the role and the company, the more excited I have become. ${goal}.

I would be so grateful if you felt comfortable referring me or connecting me with the hiring team. I understand this is a personal ask, and I want you to know that I would only reach out if I truly believed in the fit.

Thank you for your time and consideration.

${sign}`,

`${greet}

I am reaching out with a mix of excitement and humility. ${disc}

I am ${bg}, and the [Role Title] position at ${co} resonates deeply with my career goals. ${goal}. Your endorsement, even a brief introduction to the hiring manager, would mean the world to me.

I am happy to share my resume, portfolio, or anything else that would help you feel confident in making the connection. Thank you for considering this.

${sign}`,

`${greet}

${disc}

I am ${bg}, and I have to be honest -- the opportunity at ${co} is one I have been hoping to find. ${goal}. I know that a referral from someone inside the company can make a real difference, which is why I am reaching out to you.

I promise I would represent you well. If you are open to it, I would love to discuss how I might be a good fit for the team.

${sign}`,

`${greet}

${disc} I am writing with great respect for your time and position.

I am ${bg}. I recently came across the [Role Title] role at ${co}, and I believe it is a wonderful match for my skills. ${goal}. If you are comfortable providing a referral, I would be deeply grateful. If not, even a word of advice on the application process would be invaluable.

Thank you from the bottom of my heart.

${sign}`,
    ];
  }

  // ----- Industry Advice -----
  if (purpose === 'Industry Advice') {
    if (tone === 'Professional') {
      return [
`${greet}

${disc}

I am ${bg}, and I am writing to seek your perspective on the industry. Specifically, I am interested in ${goal}. Given your experience as ${title} at ${co}, I believe you are uniquely positioned to offer valuable insight.

I would be grateful for even a brief email exchange or a 15-minute call. I have a few targeted questions and promise to be respectful of your time.

Thank you for considering this request.

${sign}`,

`${greet}

I hope this message finds you well. ${disc}

As someone who is ${bg}, I am navigating an important decision point and would greatly benefit from the perspective of an experienced professional like yourself. I am particularly trying to understand ${goal}.

Would you be willing to share your thoughts, either via email or a short conversation? Your insight would be tremendously helpful.

${sign}`,

`${greet}

${disc}

I am ${bg}, and I have been closely following developments in our industry. I am reaching out because ${goal}, and I believe your vantage point as ${title} at ${co} could help me see things more clearly.

I would welcome any advice you are willing to share, in whatever format is most convenient for you.

${sign}`,

`${greet}

${disc} I am reaching out because I admire the depth of your experience in this field.

I am ${bg}, and I am currently working through ${goal}. A brief conversation or even a short email reply from someone with your expertise would go a long way in helping me navigate this effectively.

I appreciate your time and look forward to the possibility of connecting.

${sign}`,
      ];
    }

    if (tone === 'Casual') {
      return [
`${greet}

${disc}

I am ${bg}, and I have got some questions about where the industry is heading. Specifically, I am curious about ${goal}. You seem like someone who would have a really interesting take on this.

Would you be up for a quick chat? Even a few thoughts over email would be great. No pressure!

${sign}`,

`${greet}

${disc} Hope you do not mind the cold outreach!

I am ${bg}, and I am trying to get smarter about ${goal}. Your experience at ${co} makes you exactly the kind of person I would love to learn from.

Got a few minutes sometime? A quick call or even a couple of email replies would be super helpful.

${sign}`,

`${greet}

${disc}

So I am ${bg}, and honestly, I am a little stuck on ${goal}. I have been doing my own research, but there is nothing like hearing from someone who has actually been in the trenches.

Would you mind sharing your thoughts? Even a brief reply to this email would make my week.

${sign}`,

`${greet}

${disc} I know cold emails can be a lot, but I promise this one has a point!

I am ${bg}, and I am trying to figure out ${goal}. You are one of the few people I think could give me a really honest and informed perspective.

Any thoughts you are willing to share would be hugely appreciated. Thanks!

${sign}`,
      ];
    }

    // Warm
    return [
`${greet}

${disc} I hope you will forgive the unsolicited message -- your expertise genuinely inspired me to reach out.

I am ${bg}, and I am currently grappling with ${goal}. Your experience as ${title} at ${co} gives you exactly the kind of perspective I am seeking.

I would be deeply grateful for any wisdom you are willing to share. Whether it is a quick call, a few lines in an email, or even a recommended resource, it would mean a great deal.

${sign}`,

`${greet}

I am reaching out with sincere admiration for your work. ${disc}

I am ${bg}, and I am at a juncture where ${goal}. Hearing from someone who has navigated similar territory would be incredibly valuable to me.

I understand you must receive many messages like this, and I want you to know that I will treasure whatever time or insight you can offer.

${sign}`,

`${greet}

${disc}

I am ${bg}, and I feel fortunate to live in an era where I can reach out to professionals I admire. ${goal} is something I have been wrestling with, and your perspective could genuinely change the way I approach it.

Thank you for taking the time to read this. Whatever you are willing to share, I will receive with gratitude.

${sign}`,

`${greet}

${disc} Your thoughtfulness in your work has inspired me to ask for your guidance.

I am ${bg}. I am hoping to learn about ${goal}. I know that your time is precious, and I would not reach out if I did not believe your insight could make a real difference in my professional growth.

Thank you for being the kind of professional that others look up to.

${sign}`,
    ];
  }

  // ----- Mentorship -----
  if (purpose === 'Mentorship') {
    if (tone === 'Professional') {
      return [
`${greet}

${disc}

I am ${bg}, and I am seeking a mentor who can help guide my professional development. Your career trajectory as ${title} at ${co} is one I deeply admire, and I believe ${goal}.

I am not looking for a large time commitment -- even occasional guidance, perhaps a monthly check-in or periodic email exchange, would be tremendously valuable to me.

I would welcome the opportunity to discuss what a mentorship arrangement might look like. Thank you for considering this.

${sign}`,

`${greet}

I hope this message finds you well. ${disc}

As someone who is ${bg}, I have reached a point where I believe mentorship could accelerate my growth significantly. ${goal}, and your experience makes you an ideal mentor.

I am flexible on format -- whether it is a monthly call, quarterly coffee, or occasional email advice, I would be grateful for any level of engagement you are comfortable with.

${sign}`,

`${greet}

${disc}

I am ${bg}, and I am reaching out because I believe in the power of mentorship. I have identified ${goal} as a key area for my development, and your expertise as ${title} at ${co} directly aligns with what I am hoping to learn.

I am prepared to come to each interaction with specific questions and take-aways, and I am committed to respecting your time and following through on your advice.

${sign}`,

`${greet}

${disc} I am writing to explore the possibility of a mentoring relationship.

I am ${bg}. My goal is ${goal}, and I believe your guidance could help me get there more effectively. I am self-directed and proactive -- I would do the heavy lifting, and I am simply asking for the benefit of your experience and perspective.

Would you be open to an initial conversation to see if this might be a good fit? I am flexible on time and format.

${sign}`,
      ];
    }

    if (tone === 'Casual') {
      return [
`${greet}

${disc}

I am going to be upfront -- I am ${bg}, and I think you would be an amazing mentor. Your work at ${co} is exactly the kind of career I am building toward, and I am hoping ${goal}.

I am not asking for a huge commitment. Even a monthly chat or the occasional email when I am stuck would be incredible. What do you think?

${sign}`,

`${greet}

${disc} I know this is a bold ask, but here goes!

I am ${bg}, and I have been looking for someone to learn from. Your path as ${title} at ${co} is super inspiring, and I think ${goal}.

Would you be open to being a sort of informal mentor? I am talking low-key -- maybe a coffee chat every now and then. No big deal if it does not work out!

${sign}`,

`${greet}

${disc}

Real talk: I am ${bg}, and I could use some guidance. ${goal}, and I think you are the kind of person who could help me figure it out.

Any chance you would be open to an informal mentoring thing? I promise I am a quick learner and I will not waste your time.

${sign}`,

`${greet}

${disc} Hope this does not come across as too forward!

I am ${bg}, and I have been thinking about ${goal}. Your career at ${co} is seriously impressive, and I would love to learn from your experience.

Would you be up for grabbing coffee or hopping on a call sometime? Even a one-time thing would be great, but I would love to make it ongoing if you are open to it.

${sign}`,
      ];
    }

    // Warm
    return [
`${greet}

${disc} I am reaching out with a heartfelt request.

I am ${bg}, and at this stage of my career, I believe mentorship could be transformative. Your journey as ${title} at ${co} is one that deeply resonates with my aspirations, and ${goal}.

I understand that agreeing to mentor someone is a significant commitment, even in small doses. I want you to know that I would approach this with the utmost respect for your time and energy.

Would you be open to an introductory conversation? It would mean more than I can express.

${sign}`,

`${greet}

I have thought carefully about whether to send this message, and I have decided that the worst thing that can happen is a polite no. ${disc}

I am ${bg}. ${goal}, and I sincerely believe that your guidance could help me grow in ways I can not achieve on my own. Your career at ${co} represents the kind of impact I hope to make.

I am committed, coachable, and deeply grateful for any mentorship you might be willing to offer.

${sign}`,

`${greet}

${disc}

I am ${bg}, and I want to be transparent: I am looking for a mentor, and you are someone whose career I genuinely admire. ${goal} is at the forefront of my mind, and I believe your experience could help me navigate this chapter with more confidence and clarity.

Even a small amount of your time would leave a lasting impression on my career. I hope you will consider this request.

${sign}`,

`${greet}

${disc} This might be the most important email I have sent in a while.

I am ${bg}. I have reached a point where I know I need guidance from someone who has walked this path before me. ${goal}, and I think you are the perfect person to help me see the way forward.

I promise to be a respectful, prepared, and grateful mentee. Thank you for reading this.

${sign}`,
    ];
  }

  // ----- Alumni Connection -----
  if (purpose === 'Alumni Connection') {
    if (tone === 'Professional') {
      return [
`${greet}

I am a fellow [University] graduate, and ${disc}

I am ${bg}, and I am reaching out because I believe our shared alma mater provides a strong foundation for a professional connection. ${goal}, and I think your experience at ${co} could provide valuable perspective.

Would you be open to a brief conversation? I would be grateful for the chance to learn from a fellow alum who has built such an impressive career.

${sign}`,

`${greet}

As a fellow [University] alum, I was excited to discover your profile. ${disc}

I am ${bg}, and I am interested in ${goal}. Our shared educational background gives me confidence that we speak a similar professional language, and I would value the chance to learn from your experience at ${co}.

I would welcome a brief call or coffee meeting at your convenience.

${sign}`,

`${greet}

I hope this message finds you well. I noticed we are both [University] alumni, and ${disc}

I am ${bg}. ${goal}. I have always believed in the strength of alumni networks, and I think a conversation with you could be tremendously helpful as I navigate this stage of my career.

Thank you for considering this request from a fellow graduate.

${sign}`,

`${greet}

Fellow [University] alum here! ${disc}

I am ${bg}, and our shared alma mater inspired me to reach out. ${goal}, and I believe your perspective as ${title} at ${co} would be especially valuable coming from someone who understands the [University] experience.

I would appreciate any time you can spare. Thank you.

${sign}`,
      ];
    }

    if (tone === 'Casual') {
      return [
`${greet}

Go [Mascot]! Fellow [University] alum here. ${disc}

I am ${bg}, and I thought our shared background might make this a natural connection. I am hoping ${goal}. Would you be down for a quick chat?

Always great to connect with fellow alumni. Hope to hear from you!

${sign}`,

`${greet}

Hey there, fellow [University] grad! ${disc}

I am ${bg}, and when I saw the [University] connection, I figured I had to reach out. ${goal} -- and who better to ask than someone who has been through the same program?

Let me know if you have a few minutes to chat sometime. Go [Mascot]!

${sign}`,

`${greet}

${disc} And we went to the same school -- small world!

I am ${bg}, and I am currently working on ${goal}. I love connecting with fellow [University] people, and I think you could give me some really helpful perspective.

Coffee or a quick call? I am flexible!

${sign}`,

`${greet}

Nothing like the alumni network, right? ${disc}

I am ${bg}, and I am reaching out because ${goal}. Fellow alums always seem to get it, and I think you would have a great perspective.

Would love to chat if you have a few minutes. Thanks!

${sign}`,
      ];
    }

    // Warm
    return [
`${greet}

There is something special about connecting with a fellow [University] graduate. ${disc}

I am ${bg}, and discovering that we share the same alma mater made me feel a genuine connection before even reaching out. ${goal}, and I believe your experience at ${co} could offer the kind of insight I am looking for.

I would be truly grateful for a few minutes of your time. Our shared background gives me confidence that this could be a meaningful conversation.

${sign}`,

`${greet}

${disc} And as a fellow [University] alum, I feel a special bond in reaching out.

I am ${bg}. ${goal}. I have always believed that the [University] community is one of the most generous and supportive out there, and I am hoping you might be willing to extend that spirit to a fellow graduate.

Any time you can spare would mean a great deal. Thank you.

${sign}`,

`${greet}

I am reaching out as a proud [University] graduate who was delighted to discover a fellow alum doing such impressive work. ${disc}

I am ${bg}, and ${goal}. The alumni connection gives me confidence that you understand the value of lifting up fellow graduates, and I hope you will consider spending a few minutes sharing your perspective.

With gratitude from one alum to another.

${sign}`,

`${greet}

${disc} As a fellow [University] graduate, your success at ${co} fills me with pride.

I am ${bg}, and I am hoping to benefit from your wisdom. ${goal}. I know that our shared educational experience may be just one small thing we have in common, but it gave me the courage to reach out.

Thank you for being the kind of alum who inspires others.

${sign}`,
    ];
  }

  // ----- Conference Follow-up -----
  if (purpose === 'Conference Follow-up') {
    if (tone === 'Professional') {
      return [
`${greet}

It was a pleasure connecting with you at [Conference Name]. ${disc}

I am ${bg}, and our conversation about ${goal} was one of the highlights of the event for me. I would like to continue the discussion and explore how we might stay connected.

Would you be open to a follow-up call or meeting in the coming weeks? I believe there is real potential for a productive exchange of ideas.

${sign}`,

`${greet}

Thank you for the engaging conversation at [Conference Name]. ${disc}

I am ${bg}. I found our discussion about ${goal} particularly thought-provoking and would welcome the opportunity to continue it. Your perspective as ${title} at ${co} offers a viewpoint I rarely get to hear.

I hope we can find time to reconnect.

${sign}`,

`${greet}

${disc} I wanted to follow up on our conversation at [Conference Name] while it is still fresh.

I am ${bg}, and I was genuinely impressed by your insights on ${goal}. I believe continuing our discussion could be mutually beneficial, and I would welcome the chance to learn more about your work at ${co}.

Please let me know if you are available for a brief conversation.

${sign}`,

`${greet}

I hope this message finds you well after [Conference Name]. ${disc}

As someone who is ${bg}, I particularly valued your perspective on ${goal}. Our conversation reinforced my belief that professionals like us have much to gain from staying connected.

I would love to schedule a follow-up. Please let me know what works for your schedule.

${sign}`,
      ];
    }

    if (tone === 'Casual') {
      return [
`${greet}

Great meeting you at [Conference Name]! ${disc}

I am ${bg}, and I really enjoyed our chat about ${goal}. Definitely one of the best conversations I had at the event.

Would love to keep the conversation going. Coffee, call, or just email back and forth -- whatever works for you!

${sign}`,

`${greet}

${disc} What a great event, right?

I am ${bg}, and I have been thinking about our discussion on ${goal} since we talked. You had some really interesting points that I would love to dive deeper into.

Let's stay in touch! Would be awesome to grab coffee or hop on a quick call sometime.

${sign}`,

`${greet}

Hope you made it home from [Conference Name] in one piece! ${disc}

I am ${bg}, and I wanted to make sure we stayed connected after that great conversation about ${goal}. It is not every day you meet someone so thoughtful about this stuff.

Let me know if you ever want to chat more. I am always up for it!

${sign}`,

`${greet}

${disc} [Conference Name] was packed, but our conversation stood out.

I am ${bg}, and I thought what you said about ${goal} was spot on. Would love to keep the dialogue going -- maybe over a virtual coffee?

Thanks for making the conference worth it!

${sign}`,
      ];
    }

    // Warm
    return [
`${greet}

Meeting you at [Conference Name] was truly one of the highlights of the event. ${disc}

I am ${bg}, and our conversation about ${goal} left a lasting impression on me. Your insights were not only informative but genuinely inspiring.

I would love the opportunity to stay connected and continue learning from your perspective. Whether through a follow-up conversation or simply staying in touch via email, I would value the connection deeply.

${sign}`,

`${greet}

${disc} I wanted to reach out while the energy from [Conference Name] is still fresh.

I am ${bg}, and your thoughts on ${goal} resonated with me on both a professional and personal level. It is rare to have a conversation at a conference that feels so meaningful.

I hope we can find a way to continue this dialogue. It would mean a great deal to me.

${sign}`,

`${greet}

I have been reflecting on our conversation at [Conference Name], and I wanted you to know how much it meant to me. ${disc}

I am ${bg}. What you shared about ${goal} gave me a lot to think about, and I find myself wanting to continue the conversation. Your generosity in sharing your time and ideas did not go unnoticed.

I hope this is the beginning of a lasting connection.

${sign}`,

`${greet}

${disc} [Conference Name] was wonderful, but our conversation was the part I keep coming back to.

I am ${bg}, and ${goal} is something I am passionate about. Your perspective as ${title} at ${co} added dimensions I had not considered, and I am grateful for the exchange.

I would love to stay in touch and explore where this connection might lead. Thank you for your openness and warmth.

${sign}`,
    ];
  }

  // Fallback
  return [
`${greet}

${disc}

I am ${bg}, and I am reaching out because ${goal}. I would appreciate the opportunity to connect and learn from your experience.

Thank you for your time.

${sign}`,
  ];
}

// ---------------------------------------------------------------------------
// Generate function
// ---------------------------------------------------------------------------

function generateEmail(
  purpose: Purpose,
  tone: Tone,
  source: DiscoverySource,
  recipientName: string,
  recipientTitle: string,
  company: string,
  background: string,
  seeking: string,
  variationIndex?: number
): GeneratedEmail {
  const subjects = getSubjectLines(purpose, tone, recipientTitle, company, source);
  const bodies = getBodyTemplates(purpose, tone, source, recipientName, recipientTitle, company, background, seeking);

  const maxVariations = Math.min(subjects.length, bodies.length);
  const idx =
    variationIndex !== undefined
      ? variationIndex % maxVariations
      : Math.floor(Math.random() * maxVariations);

  return {
    subject: subjects[idx],
    body: bodies[idx],
    variationIndex: idx,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function NetworkingEmailContent() {
  const [purpose, setPurpose] = useState<Purpose>('Informational Interview');
  const [recipientName, setRecipientName] = useState('');
  const [recipientTitle, setRecipientTitle] = useState('');
  const [company, setCompany] = useState('');
  const [source, setSource] = useState<DiscoverySource>('LinkedIn');
  const [background, setBackground] = useState('');
  const [seeking, setSeeking] = useState('');
  const [tone, setTone] = useState<Tone>('Professional');
  const [email, setEmail] = useState<GeneratedEmail | null>(null);
  const [generated, setGenerated] = useState(false);

  const handleGenerate = () => {
    const result = generateEmail(
      purpose, tone, source, recipientName, recipientTitle, company, background, seeking
    );
    setEmail(result);
    setGenerated(true);
  };

  const handleRegenerate = () => {
    if (!email) return;
    const subjects = getSubjectLines(purpose, tone, recipientTitle, company, source);
    const bodies = getBodyTemplates(purpose, tone, source, recipientName, recipientTitle, company, background, seeking);
    const maxVariations = Math.min(subjects.length, bodies.length);
    let newIdx = Math.floor(Math.random() * maxVariations);
    while (newIdx === email.variationIndex && maxVariations > 1) {
      newIdx = Math.floor(Math.random() * maxVariations);
    }
    const result = generateEmail(
      purpose, tone, source, recipientName, recipientTitle, company, background, seeking, newIdx
    );
    setEmail(result);
  };

  const handleToneChange = (newTone: Tone) => {
    setTone(newTone);
    if (generated) {
      const result = generateEmail(
        purpose, newTone, source, recipientName, recipientTitle, company, background, seeking
      );
      setEmail(result);
    }
  };

  const fullEmailText = email ? `Subject: ${email.subject}\n\n${email.body}` : '';

  const inputClass =
    'w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow text-gray-900';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Networking Email Generator"
        description="Write personalized cold networking emails that get responses. Generate professional outreach messages for informational interviews, referrals, and connections."
        category="Job Search Tools"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Form */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Email Details</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Purpose */}
            <div className="sm:col-span-2">
              <label htmlFor="purpose" className={labelClass}>
                Purpose
              </label>
              <select
                id="purpose"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value as Purpose)}
                className={inputClass}
              >
                {purposes.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* Recipient Name */}
            <div>
              <label htmlFor="recipientName" className={labelClass}>
                Recipient Name
              </label>
              <input
                id="recipientName"
                type="text"
                placeholder="Sarah Johnson"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Recipient Title */}
            <div>
              <label htmlFor="recipientTitle" className={labelClass}>
                Their Title / Role
              </label>
              <input
                id="recipientTitle"
                type="text"
                placeholder="VP of Engineering"
                value={recipientTitle}
                onChange={(e) => setRecipientTitle(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Company */}
            <div>
              <label htmlFor="company" className={labelClass}>
                Their Company
              </label>
              <input
                id="company"
                type="text"
                placeholder="Acme Corp"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Discovery Source */}
            <div>
              <label htmlFor="source" className={labelClass}>
                How You Found Them
              </label>
              <select
                id="source"
                value={source}
                onChange={(e) => setSource(e.target.value as DiscoverySource)}
                className={inputClass}
              >
                {discoverySources.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Background */}
            <div className="sm:col-span-2">
              <label htmlFor="background" className={labelClass}>
                Your Background{' '}
                <span className="font-normal text-gray-400">(brief summary)</span>
              </label>
              <textarea
                id="background"
                rows={2}
                placeholder="e.g., a product manager with 5 years of experience in B2B SaaS, currently transitioning into fintech"
                value={background}
                onChange={(e) => setBackground(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* What You're Seeking */}
            <div className="sm:col-span-2">
              <label htmlFor="seeking" className={labelClass}>
                What You Are Seeking
              </label>
              <textarea
                id="seeking"
                rows={2}
                placeholder="e.g., learning about the culture and growth opportunities at your company, understanding how product teams are structured"
                value={seeking}
                onChange={(e) => setSeeking(e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Tone */}
            <div className="sm:col-span-2">
              <label className={labelClass}>Tone</label>
              <div className="flex flex-wrap gap-3">
                {tones.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTone(t)}
                    className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors border ${
                      tone === t
                        ? 'bg-blue-600 text-white border-blue-600 shadow'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div className="mt-8">
            <button
              onClick={handleGenerate}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-10 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-lg"
            >
              Generate Email
            </button>
          </div>
        </div>

        {/* Results Section */}
        {generated && email && (
          <div className="mt-10">
            {/* Tone Tabs + Actions */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {tones.map((t) => (
                <button
                  key={t}
                  onClick={() => handleToneChange(t)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                    tone === t
                      ? 'bg-blue-600 text-white shadow'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={handleRegenerate}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Regenerate
                </button>
                <CopyButton text={fullEmailText} label="Copy All" />
              </div>
            </div>

            {/* Email Preview Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Subject Line */}
              <div className="border-b border-gray-100 px-8 py-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Subject Line</p>
                    <p className="text-lg font-semibold text-gray-900">{email.subject}</p>
                  </div>
                  <CopyButton text={email.subject} label="Copy" className="shrink-0 mt-1" />
                </div>
              </div>

              {/* Email Body */}
              <div className="px-8 py-6 sm:px-12 sm:py-8">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Email Body</p>
                  <CopyButton text={email.body} label="Copy Body" />
                </div>
                <div className="font-serif text-gray-800 leading-relaxed whitespace-pre-line text-base sm:text-lg">
                  {email.body}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tips Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Cold Networking Etiquette</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Be Specific About Your Ask</h3>
                <p className="text-sm text-gray-600">
                  Vague requests get ignored. Instead of &quot;I would love to connect,&quot; ask for a specific 15-minute call about a specific topic. People are more likely to respond when they know exactly what you need and how long it will take.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Do Your Research First</h3>
                <p className="text-sm text-gray-600">
                  Never ask a question you could answer with a Google search. Read their LinkedIn profile, recent posts, and company news. Reference something specific to show you have done your homework -- it dramatically increases response rates.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Keep It Short</h3>
                <p className="text-sm text-gray-600">
                  Your email should take less than 30 seconds to read. Busy professionals scan their inbox quickly. A concise email that gets to the point is far more likely to receive a response than a long, detailed one.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Heart className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Lead With Value, Not Need</h3>
                <p className="text-sm text-gray-600">
                  Frame your outreach around genuine interest in their work, not just what they can do for you. Compliment their achievements, reference their content, or offer a relevant observation. Make them feel valued, not used.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Make It Easy to Say Yes</h3>
                <p className="text-sm text-gray-600">
                  Offer multiple options for meeting (phone, video, coffee). Suggest specific times. The fewer decisions they have to make, the more likely they are to respond. Remove every possible barrier to a yes.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Follow Up Once, Then Move On</h3>
                <p className="text-sm text-gray-600">
                  If you do not hear back within a week, send one polite follow-up. After that, respect their silence. Not everyone can respond, and being pushy will only damage your reputation. Focus your energy on other connections.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Personalize Every Message</h3>
                <p className="text-sm text-gray-600">
                  Mass emails are obvious and off-putting. Take the time to customize each message with details about the recipient. Mention a specific project, article, or achievement. Genuine personalization is the difference between a reply and the trash folder.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Always Send a Thank You</h3>
                <p className="text-sm text-gray-600">
                  If someone takes the time to respond or meet with you, always send a thank-you message within 24 hours. Mention something specific from the conversation and let them know how you plan to act on their advice. This turns a single interaction into a lasting connection.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Educational Content */}
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Cold Networking Works (and How to Do It Right)</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Cold networking -- reaching out to people you do not already know -- is one of the most effective yet underutilized strategies in a job search. Studies from the <a href="https://www.shrm.org/topics-tools/news/talent-acquisition/employee-referrals-remain-top-source-hires" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Society for Human Resource Management</a> consistently show that employee referrals remain the number one source of quality hires, and those referrals often begin with a networking conversation. The hidden job market -- positions filled through connections before they are ever publicly posted -- accounts for a significant share of all hires, making proactive outreach essential.
            </p>
            <p>
              The key to successful cold networking is shifting your mindset from &quot;asking for a job&quot; to &quot;building a relationship.&quot; The best cold emails are not about you; they are about the other person. Lead with genuine curiosity about their work, reference something specific you admire (a recent article, a project, a career milestone), and make a modest, time-bounded ask. Requesting a 15-minute informational interview is far more likely to get a yes than asking someone to review your resume or refer you for a job on the first contact.
            </p>
            <p>
              Response rates for cold networking emails typically range from 10% to 30%, depending on how well the message is personalized and how clear the ask is. Do not be discouraged by silence. Most people are not ignoring you maliciously -- they are simply busy. A single, polite follow-up after one week can significantly increase your response rate. After that, move on and focus your energy elsewhere. Over time, the connections that do respond often become some of the most valuable relationships in your career.
            </p>
            <h4 className="text-lg font-bold text-gray-900 pt-2">Building a Networking Strategy</h4>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Set a weekly target.</strong> Aim to send three to five personalized networking emails per week. Consistency matters more than volume.</li>
              <li><strong>Use LinkedIn strategically.</strong> Before emailing, connect on LinkedIn with a personalized note. This warms up the contact and gives them context about who you are.</li>
              <li><strong>Prepare for informational interviews.</strong> Come with three to five thoughtful questions, keep the conversation to the time you requested, and never ambush someone with a job ask during an informational meeting.</li>
              <li><strong>Always follow up with gratitude.</strong> Send a thank you within 24 hours of any conversation. Mention something specific you learned and how you plan to act on their advice.</li>
              <li><strong>Pay it forward.</strong> Networking is a two-way street. Look for ways to add value to the people you connect with, whether that means sharing a relevant article, making an introduction, or simply offering encouragement.</li>
            </ul>
            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.indeed.com/career-advice/finding-a-job/networking-email" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: How to Write a Networking Email</a>
              {' · '}
              <a href="https://hbr.org/2016/05/learn-to-love-networking" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: Learn to Love Networking</a>
              {' · '}
              <a href="https://www.thebalancemoney.com/top-tips-for-networking-your-way-to-a-job-2062604" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">The Balance: Top Tips for Networking Your Way to a Job</a>
            </p>
          </div>
        </section>

        {/* CTA */}
        <ToolCTA
          headline="Ready to supercharge your job search?"
          description="Pair great networking with a polished, ATS-friendly resume. Our AI-powered resume builder helps you stand out to recruiters and hiring managers."
          buttonText="Build My Resume Free"
          buttonHref="https://app.superpowerresume.com/sign-up"
        />
      </div>
    </div>
  );
}
